/**
 * DM-7976: vocabularies and presentation logic for the Charity Verification
 * page — the wire enums as const objects, the queue filters, the chips and
 * the small formatters. The decision form's logic (draft, validation, request
 * body, error mapping) is its sibling charityVerificationDecision.logic.ts.
 *
 * Split out for the reason moderation.logic.ts is: apps/admin-ui is never
 * ESLint'd or complexity-checked in CI, so the pure part of a page lives in a
 * sibling file that can be tested without rendering anything.
 */

import { AdminGroup } from './auditRetention.logic';
import type {
  CharityAutomatedVerdict,
  CharityCheckResult,
  CharityClaimStatus,
  CharityContactConfirmationMethod,
  CharityContactConfirmationState,
  CharityDocumentDecision,
  CharityExtractionWire,
  CharityFlagSeverity,
  CharityIndependentSourceType,
  CharityLetterKind,
  CharityRejectionReasonCode,
  CharityRubricResult,
  CharityStandingMatchKind,
  CharityVerdictWire,
  CharityVerificationCaAgMayNotOperate,
  CharityVerificationCaFtbRevoked,
  CharityVerificationDecision,
  CharityVerificationListParams,
  CharityVerificationStanding,
} from '../api/adminOps';
import type { ChipTone } from '../components/ui';

/** Every member of a wire union, keyed by itself — the const-object enum of it. */
export type EnumOf<T extends string> = { readonly [K in T]: K };

export interface Chip {
  readonly label: string;
  readonly tone: ChipTone;
}

export interface Option<T extends string> {
  readonly value: T;
  readonly label: string;
}

/** Reads for every admin group; a decision only for these two. */
export const CHARITY_VERIFICATION_DECISION_GROUPS: readonly string[] = [
  AdminGroup.ADMIN,
  AdminGroup.OPERATOR,
];

export const READ_ONLY_DECISION_TEXT =
  'Your role has read-only access — recording a decision requires the admin or admin-operator role.';

export const ClaimStatus: EnumOf<CharityClaimStatus> = {
  PENDING_DOCUMENTS: 'PENDING_DOCUMENTS',
  IN_REVIEW: 'IN_REVIEW',
  AWAITING_ORG_CONFIRMATION: 'AWAITING_ORG_CONFIRMATION',
  VERIFIED: 'VERIFIED',
  REJECTED: 'REJECTED',
  EXPIRED: 'EXPIRED',
  REVOKED: 'REVOKED',
};

export const Decision: EnumOf<CharityVerificationDecision> = {
  APPROVE: 'APPROVE',
  REJECT: 'REJECT',
  REQUEST_CONFIRMATION: 'REQUEST_CONFIRMATION',
  REVOKE: 'REVOKE',
  DISMISS_DISPUTE: 'DISMISS_DISPUTE',
  CONFIRM_SAMPLE: 'CONFIRM_SAMPLE',
};

export const RejectionReasonCode: EnumOf<CharityRejectionReasonCode> = {
  DOCUMENT_NOT_VERIFIED: 'DOCUMENT_NOT_VERIFIED',
  DOCUMENT_INVALID: 'DOCUMENT_INVALID',
  NOT_ELIGIBLE: 'NOT_ELIGIBLE',
  CONNECTION_NOT_CONFIRMED: 'CONNECTION_NOT_CONFIRMED',
};

export const RubricResult: EnumOf<CharityRubricResult> = {
  PASS: 'PASS',
  FAIL: 'FAIL',
  NOT_APPLICABLE: 'NOT_APPLICABLE',
};

export const IndependentSourceType: EnumOf<CharityIndependentSourceType> = {
  FORM_990: 'FORM_990',
  FORM_990N: 'FORM_990N',
  STATE_REGISTRY: 'STATE_REGISTRY',
  OTHER: 'OTHER',
};

export const ContactMethod: EnumOf<CharityContactConfirmationMethod> = {
  CODE: 'CODE',
  PHONE: 'PHONE',
  LETTER_OF_AUTHORIZATION: 'LETTER_OF_AUTHORIZATION',
};

export const ContactState: EnumOf<CharityContactConfirmationState> = {
  NONE: 'NONE',
  SENT: 'SENT',
  CONFIRMED: 'CONFIRMED',
  LOCKED: 'LOCKED',
  EXPIRED: 'EXPIRED',
};

export const LetterKind: EnumOf<CharityLetterKind> = {
  DETERMINATION_LETTER: 'DETERMINATION_LETTER',
  FORM_1023EZ_DETERMINATION: 'FORM_1023EZ_DETERMINATION',
  AFFIRMATION_LETTER: 'AFFIRMATION_LETTER',
  EIN_LETTER: 'EIN_LETTER',
  GROUP_EXEMPTION_LETTER: 'GROUP_EXEMPTION_LETTER',
  PARENT_CONFIRMATION: 'PARENT_CONFIRMATION',
  NOT_A_LETTER: 'NOT_A_LETTER',
  UNREADABLE: 'UNREADABLE',
};

export const DocumentDecision: EnumOf<CharityDocumentDecision> = {
  ACCEPTED: 'ACCEPTED',
  REJECTED: 'REJECTED',
};

export const FlagSeverity: EnumOf<CharityFlagSeverity> = {
  NONE: 'NONE',
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL',
};

export const CheckResult: EnumOf<CharityCheckResult> = {
  PASS: 'PASS',
  FLAG: 'FLAG',
  FAIL: 'FAIL',
  NOT_RUN: 'NOT_RUN',
  ERROR: 'ERROR',
};

/** The route's problem+json codes the page explains inline. */
export const DecisionErrorCode = {
  CLAIM_NOT_IN_STATE: 'CLAIM_NOT_IN_STATE',
  SECOND_OPERATOR_REQUIRED: 'SECOND_OPERATOR_REQUIRED',
  CLAIM_DISPUTED: 'CLAIM_DISPUTED',
  INDEPENDENT_SOURCE_REQUIRED: 'INDEPENDENT_SOURCE_REQUIRED',
  BMF_GATE_FAILED: 'BMF_GATE_FAILED',
} as const;
export type DecisionErrorCode = (typeof DecisionErrorCode)[keyof typeof DecisionErrorCode];

// ---------------------------------------------------------------------------
// Queue filters and chips
// ---------------------------------------------------------------------------

export const ALL_STATUSES = 'all';

/** The two review queues the route serves instead of a status (`review=`). */
export const ReviewQueue = {
  SAMPLE_DUE: 'SAMPLE_DUE',
  DISPUTED: 'DISPUTED',
} as const;
export type ReviewQueue = (typeof ReviewQueue)[keyof typeof ReviewQueue];

export type StatusFilter = CharityClaimStatus | ReviewQueue | typeof ALL_STATUSES;

const REVIEW_QUEUE_CHIPS: Record<ReviewQueue, { label: string; empty: string }> = {
  SAMPLE_DUE: {
    label: 'Samples to review',
    empty: 'No sampled automated approval is waiting for review.',
  },
  DISPUTED: { label: 'Disputed', empty: 'No open claim is disputed.' },
};

function isReviewQueue(filter: StatusFilter): filter is ReviewQueue {
  return filter in REVIEW_QUEUE_CHIPS;
}

/**
 * In the order the chips are shown. "All" sends no status, which the route
 * answers with its open queue (IN_REVIEW and AWAITING_ORG_CONFIRMATION); the
 * status chips each ask for one status; the two review chips ask for the
 * sampled automated approvals awaiting review (oldest due first) and the
 * disputed claims.
 */
export const STATUS_FILTERS: readonly StatusFilter[] = [
  ClaimStatus.IN_REVIEW,
  ClaimStatus.AWAITING_ORG_CONFIRMATION,
  ClaimStatus.VERIFIED,
  ClaimStatus.REJECTED,
  ClaimStatus.EXPIRED,
  ClaimStatus.REVOKED,
  ReviewQueue.SAMPLE_DUE,
  ReviewQueue.DISPUTED,
  ALL_STATUSES,
];

export const ALL_FILTER_LABEL = 'All';
export const ALL_FILTER_HINT =
  'All shows the open queue: claims in review or awaiting confirmation, highest risk first and oldest first within a risk score.';

const STATUS_CHIPS: Record<CharityClaimStatus, Chip & { hint: string }> = {
  PENDING_DOCUMENTS: {
    label: 'Pending documents',
    tone: 'neutral',
    hint: 'The claimant has not uploaded a tax-exempt letter yet.',
  },
  IN_REVIEW: {
    label: 'In review',
    tone: 'warn',
    hint: 'Documents are in; the claim waits on a reviewer.',
  },
  AWAITING_ORG_CONFIRMATION: {
    label: 'Awaiting confirmation',
    tone: 'info',
    hint: 'A confirmation code was sent to the organization’s independently sourced contact.',
  },
  VERIFIED: { label: 'Verified', tone: 'ok', hint: 'The organization is verified.' },
  REJECTED: {
    label: 'Rejected',
    tone: 'crit',
    hint: 'The claim was rejected; the claimant may resubmit unless the organization is not eligible.',
  },
  EXPIRED: {
    label: 'Expired',
    tone: 'neutral',
    hint: 'The claim lapsed before its documents arrived or its resubmission deadline passed.',
  },
  REVOKED: { label: 'Revoked', tone: 'crit', hint: 'A verified status was withdrawn.' },
};

export function statusChip(status: string): Chip & { hint: string } {
  return STATUS_CHIPS[status as CharityClaimStatus] ?? { label: status, tone: 'neutral', hint: '' };
}

export function filterLabel(filter: StatusFilter): string {
  if (filter === ALL_STATUSES) return ALL_FILTER_LABEL;
  return isReviewQueue(filter) ? REVIEW_QUEUE_CHIPS[filter].label : statusChip(filter).label;
}

/** The list params for a chip: `all` sends no status at all. */
export function queueParams(filter: StatusFilter): CharityVerificationListParams {
  if (filter === ALL_STATUSES) return {};
  return isReviewQueue(filter) ? { review: filter } : { status: filter };
}

export function emptyHint(filter: StatusFilter): string {
  if (filter === ALL_STATUSES) return 'No claim is in review or awaiting confirmation.';
  return isReviewQueue(filter)
    ? REVIEW_QUEUE_CHIPS[filter].empty
    : `No claims with status "${statusChip(filter).label}".`;
}

const SEVERITY_CHIPS: Record<CharityFlagSeverity, Chip> = {
  NONE: { label: 'No flags', tone: 'neutral' },
  LOW: { label: 'Low', tone: 'info' },
  MEDIUM: { label: 'Medium', tone: 'warn' },
  HIGH: { label: 'High', tone: 'crit' },
  CRITICAL: { label: 'Critical', tone: 'crit' },
};

export function severityChip(severity: string): Chip {
  return SEVERITY_CHIPS[severity as CharityFlagSeverity] ?? { label: severity, tone: 'neutral' };
}

const CHECK_RESULT_TONES: Record<CharityCheckResult, ChipTone> = {
  PASS: 'ok',
  FLAG: 'warn',
  FAIL: 'crit',
  NOT_RUN: 'neutral',
  ERROR: 'crit',
};

export function checkResultTone(result: string): ChipTone {
  return CHECK_RESULT_TONES[result as CharityCheckResult] ?? 'neutral';
}

const VERDICT_TONES: Record<CharityAutomatedVerdict, ChipTone> = {
  APPROVE: 'ok',
  REVIEW: 'warn',
  BOUNCE: 'crit',
};

export function verdictChip(verdict: string, mode: string): Chip {
  return {
    label: `${mode} · ${verdict}`,
    tone: VERDICT_TONES[verdict as CharityAutomatedVerdict] ?? 'neutral',
  };
}

const CONTACT_STATE_CHIPS: Record<CharityContactConfirmationState, Chip> = {
  NONE: { label: 'Not requested', tone: 'neutral' },
  SENT: { label: 'Code sent', tone: 'info' },
  CONFIRMED: { label: 'Confirmed', tone: 'ok' },
  LOCKED: { label: 'Locked', tone: 'crit' },
  EXPIRED: { label: 'Expired', tone: 'warn' },
};

export function contactStateChip(state: string): Chip {
  return (
    CONTACT_STATE_CHIPS[state as CharityContactConfirmationState] ?? {
      label: state,
      tone: 'neutral',
    }
  );
}

const HOURS_PER_DAY = 24;
const AGE_IN_HOURS_LIMIT = 48;

/** "under an hour", "7 h", "3 d" — whole numbers a queue can be scanned by. */
/**
 * Mirrors CharityOrganizationCommon.CLAIMANT_LABEL_FALLBACK_PREFIX in
 * @donatemate/charity-models: the contact emails' label for a claimant with
 * no first name.
 */
export const CLAIMANT_LABEL_FALLBACK_PREFIX = 'A representative of ';

/**
 * The claimant's name as the contact emails show it: the "First L." label,
 * else "A representative of <organization name>" (the backend sends '' when
 * the account has no first name).
 */
export function claimantLabel(displayName: string, organizationName: string): string {
  const label = displayName.trim();
  return label.length > 0 ? label : `${CLAIMANT_LABEL_FALLBACK_PREFIX}${organizationName}`;
}

export function formatAge(hours: number): string {
  if (!Number.isFinite(hours) || hours < 0) return '—';
  if (hours < 1) return 'under an hour';
  if (hours < AGE_IN_HOURS_LIMIT) return `${Math.round(hours)} h`;
  return `${Math.floor(hours / HOURS_PER_DAY)} d`;
}

export function formatConfidence(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${Math.round(value * 100)}%`;
}

const BYTES_PER_KB = 1024;

/** "12 B", "340 KB", "2.1 MB" — enough to tell a scan from a photo. */
export function formatBytes(size: number): string {
  if (!Number.isFinite(size) || size < 0) return '—';
  if (size < BYTES_PER_KB) return `${size} B`;
  const kb = size / BYTES_PER_KB;
  if (kb < BYTES_PER_KB) return `${Math.round(kb)} KB`;
  return `${(kb / BYTES_PER_KB).toFixed(1)} MB`;
}

/** The letter fields an extraction carries, with the key of each one's confidence. */
export const EXTRACTED_FIELDS: readonly {
  readonly key: keyof Pick<
    CharityExtractionWire,
    | 'extracted_ein'
    | 'extracted_name'
    | 'extracted_ruling_date'
    | 'extracted_subsection'
    | 'extracted_letter_number'
  >;
  readonly confidenceKey: string;
  readonly label: string;
}[] = [
  { key: 'extracted_ein', confidenceKey: 'ein', label: 'EIN' },
  { key: 'extracted_name', confidenceKey: 'name', label: 'Name' },
  { key: 'extracted_ruling_date', confidenceKey: 'ruling_date', label: 'Ruling date' },
  { key: 'extracted_subsection', confidenceKey: 'subsection', label: 'Subsection' },
  { key: 'extracted_letter_number', confidenceKey: 'letter_number', label: 'Letter number' },
];

export interface ExtractedFieldRow {
  readonly label: string;
  readonly value: string | null;
  readonly confidence: number | null;
}

/** One row per extracted field, with its confidence when the provider gave one. */
export function extractedFieldRows(extraction: CharityExtractionWire): ExtractedFieldRow[] {
  return EXTRACTED_FIELDS.map(field => ({
    label: field.label,
    value: extraction[field.key],
    confidence: extraction.field_confidence?.[field.confidenceKey] ?? null,
  }));
}

/** Pretty-printed JSON for a check's detail or a verdict's rubric. */
export function prettyJson(value: unknown): string {
  if (value === null || value === undefined) return '—';
  return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
}

/**
 * Whether a 60-second document link has lapsed. A missing or unparseable
 * expiry counts as lapsed, so the page offers a re-fetch rather than a dead link.
 */
export function isViewLinkExpired(expiresAt: string | null, now: number): boolean {
  if (expiresAt === null) return true;
  const expiry = new Date(expiresAt).getTime();
  return Number.isNaN(expiry) || expiry <= now;
}

// ---------------------------------------------------------------------------
// Sampled automated approvals (B3)
// ---------------------------------------------------------------------------

export const SampleReviewState = {
  DUE: 'DUE',
  OVERDUE: 'OVERDUE',
} as const;
export type SampleReviewState = (typeof SampleReviewState)[keyof typeof SampleReviewState];

const PENDING_AGREEMENT = 'PENDING';

/**
 * A sampled automated approval still waiting for its human review: DUE until
 * sample_due_at, OVERDUE after it (the P3 alarm's condition); null once a
 * reviewer agreed or disagreed, or when the verdict was not sampled.
 */
export function sampleReviewState(
  verdict: Pick<CharityVerdictWire, 'sampled_for_review' | 'sample_due_at' | 'reviewer_agreement'>,
  now: Date
): SampleReviewState | null {
  if (
    !verdict.sampled_for_review ||
    verdict.reviewer_agreement !== PENDING_AGREEMENT ||
    verdict.sample_due_at === null
  ) {
    return null;
  }
  return new Date(verdict.sample_due_at).getTime() < now.getTime()
    ? SampleReviewState.OVERDUE
    : SampleReviewState.DUE;
}

const SAMPLE_REVIEW_CHIPS: Record<SampleReviewState, Chip> = {
  DUE: { label: 'Sample review due', tone: 'warn' },
  OVERDUE: { label: 'Sample review overdue', tone: 'crit' },
};

export function sampleReviewChip(state: SampleReviewState): Chip {
  return SAMPLE_REVIEW_CHIPS[state];
}

// ---------------------------------------------------------------------------
// California good standing (DM-9613)
// ---------------------------------------------------------------------------

export const StandingMatchKind: EnumOf<CharityStandingMatchKind> = {
  FEIN: 'FEIN',
  ENTITY_NUMBER: 'ENTITY_NUMBER',
  NAME_CITY: 'NAME_CITY',
};

/** The two California lists the good-standing check reads (ExternalListKind values). */
export const StandingList = {
  CA_AG_MAY_NOT_OPERATE: 'CA_AG_MAY_NOT_OPERATE',
  CA_FTB_REVOKED: 'CA_FTB_REVOKED',
} as const;
export type StandingList = (typeof StandingList)[keyof typeof StandingList];

export const AG_REGISTRY_LABEL = 'AG Registry (CA)';
export const FTB_REVOKED_LABEL = 'FTB revoked (CA)';
export const NOT_CHECKED_TEXT = 'Not checked';
export const NOT_LISTED_TEXT = 'Not listed';
/** No hold, both lists checked, no exact match waiting out its grace. Not a confirmation of good standing. */
export const NO_ACTIVE_HOLD_TEXT = 'No active solicitation hold';
/** Neither California list has loaded yet, so nothing was evaluated. */
export const STANDING_NOT_EVALUATED_TEXT = 'Not evaluated (no California list loaded yet)';

const PART_SEPARATOR = ' · ';

/**
 * "Listed · Delinquent · CT0012345 · list of <date>" for a listed EIN (the
 * registration status, else the entity status of a Cease and Desist row),
 * "Not listed · list of <date>" otherwise, "Not checked" before the list
 * first loaded.
 */
export function agRegistryLabel(block: CharityVerificationCaAgMayNotOperate | null): string {
  if (block === null) return NOT_CHECKED_TEXT;
  const parts: (string | null)[] = block.listed
    ? ['Listed', block.registration_status ?? block.entity_status, block.registration_number]
    : [NOT_LISTED_TEXT];
  if (block.list_version_date !== null) parts.push(`list of ${block.list_version_date}`);
  return parts.filter((part): part is string => part !== null && part !== '').join(PART_SEPARATOR);
}

/**
 * "Revoked entity 1234567 · since 2019-05-15" per entity-number match (the
 * ones that list the organization), "Not listed" without one, "Not checked"
 * before the list first loaded. Name and city matches are counted apart.
 */
export function ftbRevokedLabel(block: CharityVerificationCaFtbRevoked | null): string {
  if (block === null) return NOT_CHECKED_TEXT;
  const revoked = block.matches.filter(
    match => match.match_kind === StandingMatchKind.ENTITY_NUMBER
  );
  if (revoked.length === 0) return NOT_LISTED_TEXT;
  return revoked
    .map(
      match => `Revoked entity ${match.entity_id}${PART_SEPARATOR}since ${match.revocation_date}`
    )
    .join('; ');
}

/**
 * DM-9613: the standing line when no hold is set. Never "in good standing":
 * before either list loaded nothing was evaluated, and while an exact match
 * waits out its five business days the organization is listed but not yet
 * held. Only a missing list is called out; a partly loaded pair still checks
 * the loaded one.
 */
export function standingWithoutHoldText(
  standing: CharityVerificationStanding,
  ag: CharityVerificationCaAgMayNotOperate | null,
  ftb: CharityVerificationCaFtbRevoked | null
): string {
  if (ag === null && ftb === null) return STANDING_NOT_EVALUATED_TEXT;
  const grace = standing.findings
    .map(finding => finding.enforce_after)
    .filter((when): when is string => when !== null)
    .sort();
  if (grace.length > 0) return `${NO_ACTIVE_HOLD_TEXT}: exact match in grace until ${grace[0]}`;
  return NO_ACTIVE_HOLD_TEXT;
}

/** The review-only line under the FTB field; null when no name and city coincidence was found. */
export function ftbNameCityHint(block: CharityVerificationCaFtbRevoked | null): string | null {
  const count =
    block?.matches.filter(match => match.match_kind === StandingMatchKind.NAME_CITY).length ?? 0;
  if (count === 0) return null;
  return `Name and city also match ${count} revoked ${count === 1 ? 'entity' : 'entities'} (review)`;
}

const STANDING_LIST_LABELS: Record<StandingList, string> = {
  CA_AG_MAY_NOT_OPERATE: AG_REGISTRY_LABEL,
  CA_FTB_REVOKED: FTB_REVOKED_LABEL,
};

export function standingListLabel(list: string): string {
  return STANDING_LIST_LABELS[list as StandingList] ?? list;
}

const HOLD_REASON_LABELS: Record<StandingList, string> = {
  CA_AG_MAY_NOT_OPERATE: 'AG Registry: may not operate or solicit',
  CA_FTB_REVOKED: 'FTB: exempt status revoked',
};

/** Why a charity is held, in words; an unknown reason is shown as sent. */
export function holdReasonLabel(reason: string): string {
  return HOLD_REASON_LABELS[reason as StandingList] ?? reason;
}

const MATCH_KIND_LABELS: Record<CharityStandingMatchKind, string> = {
  FEIN: 'FEIN match',
  ENTITY_NUMBER: 'entity number match',
  NAME_CITY: 'name and city (review only)',
};

export function matchKindLabel(kind: string): string {
  return MATCH_KIND_LABELS[kind as CharityStandingMatchKind] ?? kind;
}
